import { useNavigate } from 'react-router-dom';
import { AtRiskGroup, AtRiskItem, AtRiskReport } from '../../../Api/Projects/projectsApi';
import { joinWithAnd } from '../../../utils/joinWithAnd';
import { ActionNeededBox } from '../../Compounds/ActionNeededBox/ActionNeededBox';
import { ActionNeededItemRow } from '../../Compounds/ActionNeededBox/ActionNeededItemRow';

const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
const numberWord = (n: number): string => NUMBER_WORDS[n] ?? String(n);

// Known project statuses get bespoke phrasing; anything else falls back to "N in {status}".
const STATUS_PHRASES: Record<string, { singular: string; plural: string }> = {
  Escalated: { singular: 'escalation', plural: 'escalations' },
  'On Hold': { singular: 'stalled on hold', plural: 'stalled on hold' }
};

const groupPhrase = (group: AtRiskGroup): string => {
  const count = group.items.length;
  const phrase = STATUS_PHRASES[group.status];
  const label = phrase ? (count === 1 ? phrase.singular : phrase.plural) : `in ${group.status}`;
  return `${numberWord(count)} ${label}`;
};

export const buildStatusBreakdown = (groups: AtRiskGroup[]): string => joinWithAnd(groups.map(groupPhrase));

const servicesAtRisk = (count: number): string => `${count} ${count === 1 ? 'Service' : 'Services'} at Risk`;

// Both tiers share the mockup accent; the label ("Delayed" / "Possible Delay") already conveys the tier.
const RISK_BADGE_COLORS: Record<string, string> = {
  yellow: 'text-[#D96E2B]',
  red: 'text-[#D96E2B]'
};

const AtRiskItemRow = ({ item, onClick }: { item: AtRiskItem; onClick: () => void }) => {
  const breadcrumb = [item.productType, item.siteAddress, item.supplier].filter(Boolean).join(' · ');

  return (
    <ActionNeededItemRow
      testIdPrefix="at-risk"
      itemId={item.assetId}
      onOpen={onClick}>
      {breadcrumb && <div className="text-sm text-grey-10">{breadcrumb}</div>}
      <div className="text-sm mt-1">
        <span className={`${RISK_BADGE_COLORS[item.riskTier] || 'text-grey-7'} font-semibold mr-2`}>
          {item.riskLabel}
        </span>
        <span className="text-grey-9">{item.riskMessage}</span>
      </div>
      {item.nextStep && (
        <div className="text-sm mt-1">
          <span className="text-indigo font-semibold mr-2">Next Step</span>
          <span className="text-grey-9">{item.nextStep}</span>
        </div>
      )}
    </ActionNeededItemRow>
  );
};

export interface AtRiskSectionProps {
  report: AtRiskReport | null;
}

export const AtRiskSection = ({ report }: AtRiskSectionProps) => {
  const navigate = useNavigate();

  if (!report || report.atRiskCount === 0 || report.groups.length === 0) return null;

  const summary =
    `${report.atRiskCount} ${report.atRiskCount === 1 ? 'service needs' : 'services need'} attention — ` +
    `${buildStatusBreakdown(report.groups)}. Review the remediation steps below.`;

  return (
    <ActionNeededBox
      title="At Risk — Action Needed"
      count={servicesAtRisk(report.atRiskCount)}
      summary={summary}
      testIdPrefix="at-risk"
      contentKey={report}>
      {report.groups.map((group) => (
        <div
          key={group.status}
          className="border-l-[3px] border-[#D96E2B] pl-4"
          data-testid={`at-risk-group-${group.status}`}>
          <div className="text-sm font-bold text-grey-9 mb-1">{group.status}</div>
          <div className="flex flex-col gap-3">
            {group.items.map((item) => (
              <AtRiskItemRow
                key={item.assetId}
                item={item}
                onClick={() => navigate(`/projects/${encodeURIComponent(item.projectId)}`)}
              />
            ))}
          </div>
        </div>
      ))}
    </ActionNeededBox>
  );
};
